"use client";

/**
 * Authentication utility functions
 * Handles token storage using document cookies and localStorage for server and client accessibility
 */

const TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const ADDRESS_KEY = "address";

/**
 * Helper to set a cookie on the client
 */
export function setCookie(name: string, value: string, days = 7) {
  if (typeof window === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const isSecure = window.location.protocol === "https:";

  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${
    isSecure ? "; Secure" : ""
  }`;
}

/**
 * Helper to read a cookie on the client
 */
function getCookie(name: string): string | null {
  if (typeof window === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Helper to delete a cookie on the client
 */
function deleteCookie(name: string) {
  if (typeof window === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

/**
 * Store access token, refresh token, and address in cookies and localStorage
 */
export function storeAuthToken(
  token: string,
  address: string,
  refreshToken?: string,
) {
  if (typeof window === "undefined") return;

  if (token && token !== "undefined" && token !== "null" && token.trim() !== "") {
    setCookie(TOKEN_KEY, token.trim());
    localStorage.setItem(TOKEN_KEY, token.trim());
  } else {
    deleteCookie(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }

  if (refreshToken && refreshToken !== "undefined" && refreshToken !== "null" && refreshToken.trim() !== "") {
    setCookie(REFRESH_TOKEN_KEY, refreshToken.trim());
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken.trim());
  } else {
    deleteCookie(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  if (address && address !== "undefined" && address !== "null" && address.trim() !== "") {
    setCookie(ADDRESS_KEY, address.trim());
    localStorage.setItem(ADDRESS_KEY, address.trim());
  } else {
    deleteCookie(ADDRESS_KEY);
    localStorage.removeItem(ADDRESS_KEY);
  }

  window.dispatchEvent(new Event("cookie-update"));
  window.dispatchEvent(new Event("local-storage-update"));
  window.dispatchEvent(new Event("localStorageUpdated"));
}

/**
 * Get stored access token from cookie or localStorage
 */
export function getAuthToken(): string | null {
  const cookieToken = getCookie(TOKEN_KEY);
  if (
    cookieToken &&
    cookieToken !== "undefined" &&
    cookieToken !== "null" &&
    cookieToken.trim() !== ""
  ) {
    return cookieToken.trim();
  }
  if (typeof window !== "undefined") {
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (
      localToken &&
      localToken !== "undefined" &&
      localToken !== "null" &&
      localToken.trim() !== ""
    ) {
      return localToken.trim();
    }
  }
  return null;
}

/**
 * Get stored address from cookie or localStorage
 */
export function getStoredAddress(): string | null {
  return (
    getCookie(ADDRESS_KEY) ||
    (typeof window !== "undefined" ? localStorage.getItem(ADDRESS_KEY) : null)
  );
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Clear authentication cookies and localStorage
 */
export function clearAuth() {
  if (typeof window === "undefined") return;

  deleteCookie(TOKEN_KEY);
  deleteCookie(REFRESH_TOKEN_KEY);
  deleteCookie(ADDRESS_KEY);

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ADDRESS_KEY);

  window.dispatchEvent(new Event("cookie-update"));
  window.dispatchEvent(new Event("local-storage-update"));
  window.dispatchEvent(new Event("localStorageUpdated"));
}

/**
 * Get stored refresh token from cookie or localStorage
 */
export function getRefreshToken(): string | null {
  const cookieToken = getCookie(REFRESH_TOKEN_KEY);
  if (
    cookieToken &&
    cookieToken !== "undefined" &&
    cookieToken !== "null" &&
    cookieToken.trim() !== ""
  ) {
    return cookieToken.trim();
  }
  if (typeof window !== "undefined") {
    const localToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (
      localToken &&
      localToken !== "undefined" &&
      localToken !== "null" &&
      localToken.trim() !== ""
    ) {
      return localToken.trim();
    }
  }
  return null;
}

/**
 * Update stored access token (and optional refresh token) in cookies and localStorage
 */
export function updateAuthTokens(accessToken: string, refreshToken?: string) {
  if (typeof window === "undefined") return;

  setCookie(TOKEN_KEY, accessToken);
  localStorage.setItem(TOKEN_KEY, accessToken);

  if (refreshToken) {
    setCookie(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  window.dispatchEvent(new Event("cookie-update"));
  window.dispatchEvent(new Event("local-storage-update"));
  window.dispatchEvent(new Event("localStorageUpdated"));
}

/**
 * Call backend to refresh access token using stored refresh token
 */
export async function refreshAccessToken(): Promise<{
  accessToken: string;
  refreshToken?: string;
} | null> {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) return null;

  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
  if (!backendUrl) return null;

  try {
    const response = await fetch(`${backendUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentRefreshToken}`,
      },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const newAccessToken =
      data?.data?.token?.accessToken ||
      data?.token?.accessToken ||
      data?.data?.accessToken ||
      data?.accessToken;
    const newRefreshToken =
      data?.data?.token?.refreshToken ||
      data?.token?.refreshToken ||
      data?.data?.refreshToken ||
      data?.refreshToken ||
      currentRefreshToken;

    if (!newAccessToken) return null;

    updateAuthTokens(newAccessToken, newRefreshToken);
    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    };
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    return null;
  }
}

/**
 * Get authorization header for API requests
 */
export function getAuthHeaders() {
  const token = getAuthToken();
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json",
  };
}
